import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");

  const { data: booking, isLoading } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("*, availability_slots(*), charging_spots(title, address, rules)")
        .eq("id", bookingId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!bookingId,
    refetchInterval: (query) => {
      // Poll until status is paid
      const d = query.state.data as any;
      return d?.status === "paid" ? false : 3000;
    },
  });

  const spot = booking?.charging_spots as any;
  const slot = booking?.availability_slots;
  const isPaid = booking?.status === "paid";

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <p className="text-muted-foreground">Lädt Buchungsdetails…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        {isPaid ? (
          <CheckCircle className="mx-auto h-16 w-16 text-primary" />
        ) : (
          <AlertCircle className="mx-auto h-16 w-16 text-yellow-500" />
        )}
        <h1 className="text-2xl font-bold text-foreground">
          {isPaid ? "Zahlung bestätigt!" : "Zahlung wird verarbeitet…"}
        </h1>
        <p className="text-muted-foreground">
          {isPaid
            ? "Ihr Ladeslot wurde erfolgreich bezahlt und reserviert."
            : "Wir warten auf die Zahlungsbestätigung."}
        </p>

        {booking && (
          <div className="rounded-xl border border-border bg-card p-5 text-left space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-foreground">{spot?.title}</h2>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  isPaid
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {isPaid ? "Bezahlt" : "Ausstehend"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{spot?.address}</p>
            {slot && (
              <p className="text-sm text-muted-foreground">
                {format(new Date(slot.start_time), "EEEE, dd. MMMM yyyy", {
                  locale: de,
                })}
                <br />
                {format(new Date(slot.start_time), "HH:mm")} –{" "}
                {format(new Date(slot.end_time), "HH:mm")} Uhr
              </p>
            )}
            <p className="text-lg font-bold text-foreground">
              {Number(booking.amount).toFixed(2)} €
            </p>
            {spot?.rules && (
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs font-medium text-foreground">
                  Regeln des Hosts:
                </p>
                <p className="text-xs text-muted-foreground">{spot.rules}</p>
              </div>
            )}
          </div>
        )}

        <Link to="/dashboard">
          <Button className="gap-2">
            Zum Dashboard <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default BookingSuccess;
